import React, { useEffect, useState } from 'react';
import { aiService } from '../../../services/aiService';
import { useAiRequest } from './useAiRequest';
import { AiBox, AiSection, AiBullets } from './AiBox';
import { Spinner } from '../../ui/Spinner';
import { Alert } from '../../ui/Alert';

const TASKS = [
  { id: 'summarise', label: 'Summarise this Moment', title: 'Summary' },
  { id: 'followups', label: 'Suggest follow-ups', title: 'Follow-ups' },
  { id: 'reflect', label: 'Why it may matter', title: 'Reflection' }
];

const toText = (task, r) => {
  if (task === 'summarise') return [r.summary, ...(r.key_points || []).map((p) => `- ${p}`)].filter(Boolean).join('\n');
  if (task === 'followups') {
    return [...(r.commitments || []).map((c) => `- ${c.text}`), ...(r.next_steps || []).map((s) => `- ${s}`)].join('\n');
  }
  return r.reflection || '';
};

const Result = ({ task, r }) => {
  if (task === 'summarise') {
    return (
      <>
        {r.summary && <p>{r.summary}</p>}
        {r.key_points?.length > 0 && <AiSection label="Key points"><AiBullets items={r.key_points} /></AiSection>}
        {r.topics?.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {r.topics.map((t) => <span key={t} className="rounded-full bg-white border border-violet-200 px-2.5 py-0.5 text-xs font-medium text-violet-700">{t}</span>)}
          </div>
        )}
      </>
    );
  }
  if (task === 'followups') {
    const none = !r.commitments?.length && !r.next_steps?.length;
    return (
      <>
        {r.commitments?.length > 0 && (
          <AiSection label="Commitments mentioned in your note">
            <div className="space-y-2">
              {r.commitments.map((c, i) => (
                <div key={i}>
                  <p>{c.text}</p>
                  <p className="text-xs italic text-slate-500 break-words">You wrote: “{c.quote}”</p>
                </div>
              ))}
            </div>
          </AiSection>
        )}
        {r.next_steps?.length > 0 && <AiSection label="Possible next steps"><AiBullets items={r.next_steps} /></AiSection>}
        {none && <p>Nothing in this note points to a commitment or a clear next step.</p>}
      </>
    );
  }
  return <p>{r.reflection || 'There is not enough in this note to reflect on.'}</p>;
};

export const MomentAiPanel = ({ moment }) => {
  const ai = useAiRequest();
  const [task, setTask] = useState(null);
  const { reset } = ai;

  useEffect(() => { reset(); setTask(null); }, [moment.id, reset]);

  if (!(moment.body || '').trim()) return null;

  const start = (id) => { setTask(id); ai.run(() => aiService.analyseMoment(moment.id, id)); };
  const current = TASKS.find((t) => t.id === task);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {TASKS.map((t) => (
          <button
            key={t.id}
            type="button"
            disabled={ai.loading}
            onClick={() => start(t.id)}
            className="rounded-full border border-violet-200 bg-violet-50 px-3 py-1 text-xs font-semibold text-violet-700 hover:bg-violet-100 disabled:opacity-50"
          >
            {t.label}
          </button>
        ))}
      </div>
      {ai.loading && (
        <p className="flex items-center gap-2 text-xs text-slate-500"><Spinner size="sm" /> Reading your note…</p>
      )}
      {ai.error && <Alert variant="error" title="AI is unavailable" message={ai.error} />}
      {ai.data && current && (
        <AiBox
          title={current.title}
          meta={ai.data.meta}
          copyText={ai.data.insufficient ? '' : toText(task, ai.data.result)}
          onDismiss={ai.reset}
        >
          {ai.data.insufficient ? <p>{ai.data.message}</p> : <Result task={task} r={ai.data.result} />}
        </AiBox>
      )}
    </div>
  );
};